import { useState } from "react";
import { SOCIALS, IMAGES, BAND, PHONE_DISPLAY, PHONE_TEL } from "../lib/content";
import { FadeUp, MaskReveal } from "./Reveal";
import { scrollToId } from "../hooks/useLenis";
import { Send, Loader2, Check } from "lucide-react";

const FORM_ENDPOINT = "https://formsubmit.co/ajax/travelersgrthessaloniki@yahoo.com";
const CONTACT_EMAIL = "travelersgrthessaloniki@yahoo.com";

const initial = { name: "", email: "", phone: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [error, setError] = useState("");

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus("error");
      setError("Συμπλήρωσε όνομα, email και μήνυμα.");
      return;
    }
    setStatus("sending");
    setError("");
    try {
      const res = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          message: form.message,
          _subject: `Νέο μήνυμα από ${form.name} — Travelers gr`,
          _template: "table",
          _captcha: "false",
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === "false" || data.success === false) {
        throw new Error("send failed");
      }
      setStatus("sent");
      setForm(initial);
      setTimeout(() => setStatus("idle"), 6000);
    } catch (err) {
      setStatus("error");
      setError("Κάτι πήγε στραβά. Δοκίμασε ξανά ή στείλε email απευθείας.");
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-[var(--line)] py-3 font-body text-[var(--fg)] placeholder:text-[#a89f92] focus:border-[var(--gold)] focus:outline-none transition-colors duration-300";

  return (
    <footer
      id="contact"
      data-testid="contact-section"
      className="relative overflow-hidden bg-[var(--bg)] px-6 md:px-12 pt-28 md:pt-44 pb-12"
    >
      <img
        src={IMAGES.worship}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.08] kenburns"
      />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[var(--bg)] via-transparent to-[var(--bg)]" />

      <div className="relative mx-auto max-w-[1500px]">
        <FadeUp className="mb-6 flex items-center gap-6">
          <span className="h-px w-12 bg-[var(--gold)]" />
          <span className="font-mono text-[11px] tracking-[0.35em] text-[var(--gold-ink)] uppercase">
            Επικοινωνία
          </span>
        </FadeUp>

        <h2 className="font-serif leading-[0.9] text-[var(--fg)] text-[16vw] md:text-[13vw] lg:text-[10rem]">
          <MaskReveal>Ταξίδεψε</MaskReveal>
          <MaskReveal delay={0.08} className="italic text-[var(--gold-ink)]">
            μαζί μας.
          </MaskReveal>
        </h2>

        <div className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 border-t border-[var(--line)] pt-14">
          {/* Left: info + socials */}
          <div className="md:col-span-5">
            <FadeUp>
              <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-[var(--muted)] mb-4">
                Κρατήσεις & Εκδηλώσεις
              </p>
              <a
                data-testid="contact-email"
                href={`mailto:${CONTACT_EMAIL}`}
                className="font-serif text-2xl md:text-3xl text-[var(--fg)] hover:text-[var(--gold-ink)] transition-colors duration-300 link-underline break-all"
              >
                {CONTACT_EMAIL}
              </a>
              <a
                data-testid="contact-phone"
                href={`tel:${PHONE_TEL}`}
                className="mt-4 block font-serif text-2xl md:text-3xl text-[var(--fg)] hover:text-[var(--gold-ink)] transition-colors duration-300 link-underline w-fit"
              >
                {PHONE_DISPLAY}
              </a>
              <p className="mt-6 font-body text-[var(--muted)]">
                {BAND.city}, Ελλάδα · {BAND.since}
              </p>
            </FadeUp>

            <FadeUp delay={0.1} className="mt-12">
              <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-[var(--muted)] mb-6">
                Ακολούθησέ μας
              </p>
              <ul className="grid grid-cols-2 gap-px border-t border-l border-[var(--line)]">
                {SOCIALS.map((s) => (
                  <li key={s.label}>
                    <a
                      data-testid={`social-${s.label.toLowerCase()}`}
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center justify-between border-b border-r border-[var(--line)] px-5 py-5 hover:bg-[var(--surface)] transition-colors duration-300"
                    >
                      <span className="font-serif text-lg md:text-xl text-[var(--fg)] group-hover:text-[var(--gold-ink)] transition-colors duration-300">
                        {s.label}
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            </FadeUp>
          </div>

          {/* Right: form */}
          <div className="md:col-span-7">
            <FadeUp delay={0.1}>
              <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-[var(--muted)] mb-8">
                Στείλε μας μήνυμα
              </p>
              <form data-testid="contact-form" onSubmit={onSubmit} className="space-y-7">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
                  <input
                    data-testid="contact-input-name"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    placeholder="Το όνομά σου *"
                    className={inputCls}
                  />
                  <input
                    data-testid="contact-input-email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    placeholder="Email *"
                    className={inputCls}
                  />
                </div>
                <input
                  data-testid="contact-input-phone"
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  placeholder="Τηλέφωνο (προαιρετικό)"
                  className={inputCls}
                />
                <textarea
                  data-testid="contact-input-message"
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  placeholder="Το μήνυμά σου *"
                  rows={4}
                  className={`${inputCls} resize-none`}
                />

                <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-2">
                  <button
                    data-testid="contact-submit"
                    type="submit"
                    disabled={status === "sending"}
                    className="group inline-flex items-center gap-3 self-start rounded-full bg-[var(--gold)] px-8 py-4 font-mono text-[11px] tracking-[0.3em] uppercase text-[#1a1814] hover:bg-[var(--gold-soft)] disabled:opacity-60 transition-colors duration-300"
                  >
                    {status === "sending" ? (
                      <>
                        <Loader2 size={16} className="animate-spin" /> Αποστολή…
                      </>
                    ) : status === "sent" ? (
                      <>
                        <Check size={16} /> Στάλθηκε
                      </>
                    ) : (
                      <>
                        <Send size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
                        Αποστολή
                      </>
                    )}
                  </button>

                  {status === "sent" && (
                    <span data-testid="contact-success" className="font-body text-sm text-[var(--gold-ink)]">
                      Ευχαριστούμε! Θα επικοινωνήσουμε σύντομα.
                    </span>
                  )}
                  {status === "error" && (
                    <span data-testid="contact-error" className="font-body text-sm text-[var(--ember)]">
                      {error}
                    </span>
                  )}
                </div>
              </form>
            </FadeUp>
          </div>
        </div>

        <div className="mt-24 flex flex-col md:flex-row items-center justify-between gap-6 border-t border-[var(--line)] pt-8">
          <button
            data-testid="back-to-top"
            onClick={() => scrollToId("hero")}
            className="font-serif text-2xl tracking-[0.2em] text-[var(--fg)] hover:text-[var(--gold-ink)] transition-colors duration-300"
          >
            ΤΑΞΙΔΙΩΤΕΣ
          </button>
          <p className="font-mono text-[10px] tracking-[0.25em] text-[var(--muted)] uppercase text-center">
            © {new Date().getFullYear()} Travelers gr · Gospel Music Band · Made in Thessaloniki
          </p>
        </div>
      </div>
    </footer>
  );
}
